import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import './BillingDashboard.css';

interface OverdueInvoice {
  id: string;
  invoice_number: string;
  due_date: string;
  balance_due: number;
  student_name?: string;
  parent_name?: string;
}

interface OverdueAlertsProps {
  onViewOverdue?: () => void;
  onSendReminders?: () => void;
  isAdmin?: boolean;
}

const OverdueAlerts: React.FC<OverdueAlertsProps> = ({
  onViewOverdue,
  onSendReminders,
  isAdmin = false
}) => {
  const [overdueInvoices, setOverdueInvoices] = useState<OverdueInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOverdueInvoices();
  }, []);

  const fetchOverdueInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/invoices/overdue`,
        { headers }
      );
      setOverdueInvoices(response.data || []);
    } catch (err: any) {
      console.error('Error fetching overdue invoices:', err);
      setError(err.response?.data?.detail || 'Failed to load overdue invoices');
    } finally {
      setLoading(false);
    }
  };

  const totalOverdueAmount = overdueInvoices.reduce(
    (sum, inv) => sum + parseFloat(inv.balance_due.toString()),
    0
  );

  if (loading) {
    return <div className="loading">Loading overdue alerts...</div>;
  }

  if (overdueInvoices.length === 0) {
    return null;
  }

  return (
    <div className="overdue-alerts-container">
      <div className="overdue-alerts-header">
        <div className="overdue-alerts-title">
          <span className="alert-icon">⚠️</span>
          <h3>Overdue Invoices</h3>
        </div>
        <div className="overdue-alerts-summary">
          <span className="overdue-count">{overdueInvoices.length} invoices</span>
          <span className="overdue-amount">${totalOverdueAmount.toFixed(2)}</span>
        </div>
      </div>
      
      <div className="overdue-alerts-body">
        <p className="overdue-alerts-message">
          You have {overdueInvoices.length} overdue invoice{overdueInvoices.length !== 1 ? 's' : ''} 
          totaling ${totalOverdueAmount.toFixed(2)}.
        </p>
        
        {overdueInvoices.length > 0 && (
          <div className="overdue-invoices-preview">
            {overdueInvoices.slice(0, 5).map((invoice) => (
              <div key={invoice.id} className="overdue-invoice-item">
                <span className="invoice-number">{invoice.invoice_number}</span>
                <span className="invoice-balance">${invoice.balance_due.toFixed(2)}</span>
              </div>
            ))}
            {overdueInvoices.length > 5 && (
              <div className="overdue-more">
                +{overdueInvoices.length - 5} more
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="overdue-alerts-actions">
        {onViewOverdue && (
          <button className="btn btn-primary" onClick={onViewOverdue}>
            View All Overdue
          </button>
        )}
        {isAdmin && onSendReminders && (
          <button className="btn btn-secondary" onClick={onSendReminders}>
            Send Reminder Emails
          </button>
        )}
      </div>
    </div>
  );
};

export default OverdueAlerts;

